/*
 * Where a Bitbucket token points (plan P34), without typing slugs: the choices discovery found, and the workspace and
 * repository read from a URL pasted from the browser (the fallback when Bitbucket does not list what the token reaches).
 * Pure: no React.
 */
import type { ScmRepository } from '@/api/schemas';
import type { ScmTokenKind } from './tokenGuideModel';

const SLUG = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,127}$/;

export interface ScmSource {
  workspace: string;
  /** Absent for a workspace (a workspace token, or a workspace URL). */
  repository?: string;
}

/** Bitbucket pages under a workspace that are not repositories (`bitbucket.org/<ws>/workspace/...`, `/<ws>/profile/...`). */
const NOT_A_REPOSITORY = new Set(['workspace', 'profile', 'projects', 'repositories', 'snippets']);

function pathOf(text: string): string | null {
  const t = text.trim();
  const ssh = /^git@bitbucket\.org:(.+)$/i.exec(t);
  if (ssh) return ssh[1]!;
  try {
    const url = new URL(/^[a-z]+:\/\//i.test(t) ? t : `https://${t}`);
    return /(^|\.)bitbucket\.org$/i.test(url.hostname) ? url.pathname : null;
  } catch {
    return null;
  }
}

/**
 * The workspace and repository of a Bitbucket URL: `https://bitbucket.org/<ws>/<repo>/src/...`, `<ws>/<repo>.git` clone
 * URLs (https or `git@bitbucket.org:`), or a workspace page (`bitbucket.org/<ws>/`, `/<ws>/workspace/overview`). Null when
 * it is not a Bitbucket URL.
 */
export function parseBitbucketUrl(text: string): ScmSource | null {
  const path = pathOf(text);
  if (path === null) return null;
  const [workspace, second] = path.split('/').filter(Boolean);
  if (!workspace || !SLUG.test(workspace)) return null;
  const repository = second?.replace(/\.git$/i, '');
  if (!repository || NOT_A_REPOSITORY.has(repository) || !SLUG.test(repository)) return { workspace };
  return { workspace, repository };
}

export interface SourceChoice extends ScmSource {
  key: string;
  label: string;
  detail: string;
}

/** What to pick from what the token reaches: its repositories (repository token) or its workspaces (workspace token). */
export function discoveryChoices(kind: ScmTokenKind, repositories: readonly ScmRepository[]): SourceChoice[] {
  if (kind === 'repository') {
    return repositories.map((r) => ({ key: `${r.workspace}/${r.slug}`, workspace: r.workspace, repository: r.slug, label: r.name, detail: `bitbucket.org/${r.workspace}/${r.slug}` }));
  }
  const counts = new Map<string, number>();
  for (const r of repositories) counts.set(r.workspace, (counts.get(r.workspace) ?? 0) + 1);
  return [...counts].map(([workspace, n]) => ({ key: workspace, workspace, label: workspace, detail: `${n} repositor${n === 1 ? 'y' : 'ies'} · bitbucket.org/${workspace}` }));
}

/** Whether the form's workspace and repository are this choice. */
export const isChosen = (c: ScmSource, v: { workspace: string; repository: string }): boolean => c.workspace === v.workspace && (c.repository ?? '') === v.repository;

/** The values a choice or a parsed URL puts in the form for this token kind (a workspace token keeps no repository). */
export const sourceValues = (kind: ScmTokenKind, s: ScmSource): { workspace: string; repository: string } => ({ workspace: s.workspace, repository: kind === 'repository' ? (s.repository ?? '') : '' });
